import type { Stack } from "./items";

export type Phase = "waiting" | "countdown" | "playing" | "deathmatch" | "ended";

export const SIGN_LINES = 4;
export const SIGN_LINE_LEN = 15;
export const NAME_RE = /^[A-Za-z0-9_]{3,16}$/;
/** World ids are what goes in the tweeted link: /w/<id>. */
export const WORLD_ID_RE = /^[a-z0-9]{3,16}$/;
/** The random per-device secret that claims a name (see server/stats.ts). */
export const KEY_RE = /^[A-Za-z0-9_-]{16,64}$/;

export interface PlayerStats {
  kills: number;
  deaths: number;
  wins: number;
  games: number;
}

export interface LeaderRow {
  name: string;
  wins: number;
  kills: number;
}

/** A name as seen from one device: unclaimed, claimed by this device, or someone else's. */
export type NameStatus = "free" | "yours" | "taken";

export interface WorldInfo {
  id: string;
  name: string;
  players: number;
  phase: Phase;
}

export interface RoomInfo {
  id: string;
  name: string;
  players: number;
  max: number;
  phase: Phase;
}

export interface PlayerInfo {
  id: number;
  name: string;
  bot: boolean;
  alive: boolean;
  armor: (string | null)[];
}

export interface ChestInfo {
  id: number;
  x: number;
  y: number;
  z: number;
  open: boolean;
}

export interface SignInfo {
  id: number;
  x: number;
  y: number;
  z: number;
  /** -1 = standing sign on top of a block, 0..3 = wall sign facing +z,-z,+x,-x */
  wall: number;
  /** standing sign rotation in 16ths of a turn */
  rot: number;
  lines: string[];
  by: string;
}

/** An arrow (or fishing bobber). Sent when it's launched and again whenever its state changes. */
export interface ProjInfo {
  id: number;
  kind: "arrow" | "bobber";
  /** Who shot or cast it. */
  by: number;
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  /** 0 = flying, 1 = stuck in a block (or floating), 2 = hooked onto player `hook`. */
  state: 0 | 1 | 2;
  hook?: number;
  /** A fully drawn arrow: it trails crit sparks. */
  crit?: boolean;
}

// Snapshot row: id, x, y, z, yaw, pitch, flags (1=sneak, 2=swing, 4=drawing a bow), held item id, health (0..20)
export type SnapRow = [number, number, number, number, number, number, number, string, number];

export type ClientMsg =
  /** key: this device's secret, which claims the name (absent if storage is blocked). */
  | { t: "hello"; name: string; key?: string }
  | { t: "join"; world: string; name: string; key?: string }
  | { t: "leave" }
  | { t: "pos"; x: number; y: number; z: number; yaw: number; pitch: number; sneak: boolean }
  | { t: "slot"; i: number }
  | { t: "swing" }
  /** crit: the attacker was falling when they swung (jump-crit). */
  | { t: "attack"; target: number; crit?: boolean }
  | { t: "open"; chest: number }
  | { t: "eat" }
  | { t: "sign"; x: number; y: number; z: number; wall: number; rot: number; lines: string[] }
  | { t: "breakSign"; id: number }
  | { t: "fall"; d: number }
  /** Started (on) or gave up (off) pulling back the held bow. */
  | { t: "draw"; on: boolean }
  /** Let go of the bowstring, aiming this way. */
  | { t: "shoot"; yaw: number; pitch: number }
  /** Fishing rod: cast this way, or reel in if the bobber is already out. */
  | { t: "rod"; yaw: number; pitch: number };

export type ServerMsg =
  | { t: "welcome"; id: number; name: string }
  | { t: "rooms"; rooms: RoomInfo[] }
  | { t: "joined"; world: string; room: string; roomName: string; me: string; seed: number; players: PlayerInfo[]; chests: ChestInfo[]; signs: SignInfo[]; phase: Phase; timeLeft: number }
  | { t: "phase"; phase: Phase; timeLeft: number }
  | { t: "snap"; p: SnapRow[] }
  | { t: "pjoin"; p: PlayerInfo }
  | { t: "pleave"; id: number }
  | { t: "pinfo"; p: PlayerInfo }
  | { t: "self"; hp: number; hotbar: Stack[]; armor: (string | null)[]; slot: number }
  | { t: "tp"; x: number; y: number; z: number; yaw: number; frozen: boolean }
  | { t: "hurt"; kx: number; ky: number; kz: number }
  | { t: "hit"; id: number; crit: boolean }
  | { t: "chest"; id: number; open: boolean }
  | { t: "chestAdd"; c: ChestInfo }
  | { t: "loot"; items: string[] }
  | { t: "signAdd"; s: SignInfo }
  | { t: "signDel"; id: number }
  | { t: "feed"; msg: string }
  /** A sign was placed: it shows up in the chat log as `<from> text`, the only way anything gets into chat. */
  | { t: "chat"; from: string; text: string }
  | { t: "alive"; n: number }
  | { t: "win"; name: string | null }
  | { t: "toast"; msg: string }
  /** Join refused: another device owns this name. */
  | { t: "nameTaken"; name: string }
  /** Halfway through the games every chest was restocked (their lids close via "chest" messages). */
  | { t: "refill" }
  | { t: "proj"; p: ProjInfo }
  /** pop: someone picked it up. */
  | { t: "projDel"; id: number; pop?: boolean }
  /** Your arrow hit someone. */
  | { t: "ding" };
